import {isBefore, isValid, differenceInCalendarDays} from "date-fns";
import * as filterAttributes from "./filterAttributes";
import FILTER_KEYS from "./constants";
import {DateFormatter, DateParser} from "../date";


const selectMinNightsRule = (rules) => {
    if (Array.isArray(rules) && rules.length) {
        return rules.reduce((acc, x) => x[FILTER_KEYS.minNightsLimit] > acc[FILTER_KEYS.minNightsLimit] ? x : acc)
    } else {
        return null;
    }
};

const selectMinPeopleRule = (rules) => {
    if (Array.isArray(rules) && rules.length) {
        return rules.reduce((acc, x) => x[FILTER_KEYS.minPeopleLimit] > acc[FILTER_KEYS.minPeopleLimit] ? x : acc)
    } else {
        return null;
    }
};

const selectMaxPeopleRule = (rules) => {
    if (Array.isArray(rules) && rules.length) {
        return rules.reduce((acc, x) => x[FILTER_KEYS.maxPeopleLimit] < acc[FILTER_KEYS.maxPeopleLimit] ? x : acc)
    } else {
        return null;
    }
};

const selectMaxPetsRule = (rules) => {
    if (Array.isArray(rules) && rules.length) {
        return rules.reduce((acc, x) => x[FILTER_KEYS.maxPetsLimit] < acc[FILTER_KEYS.maxPetsLimit] ? x : acc)
    } else {
        return null;
    }
};

const selectBookingRule = (rules) => {
    if (Array.isArray(rules) && rules.length && rules.length > 0 ) {
        return rules[0]
    } else {
        return null;
    }
};

const getInitialRule = (rule) => {
    const emptyRule = {
        ruleType: null,
        startDay: null,
        endDay: null,
        minNightsLimit: null,
        minPeopleLimit: null,
        maxPeopleLimit: null,
        maxPetsLimit: null
    };

    return Object.assign(emptyRule, rule)
};

const getRulePeriod = (rule) => {
    const {startDay, endDay} = rule;
    const start = DateParser.safeParseISO(startDay, null);
    const end = DateParser.safeParseISO(endDay, null);
    return [start, end]
};

const getRuleMeaning = (rule, formatter = (value) => value) => {
    const {ruleType, startDay, endDay, minNightsLimit, minPeopleLimit, maxPeopleLimit, maxPetsLimit} = rule;

    const startDate = startDay ? DateParser.safeParseISO(startDay) : null;
    const endDate = endDay ? DateParser.safeParseISO(endDay) : null;
    const start = isValid(startDate) ? DateFormatter.longString(startDate) : "[Data inizio]";
    const end = isValid(endDate) ? DateFormatter.longString(endDate) : "[Data fine]";

    switch (ruleType) {
        case 1:
            return ["Non inviarmi richieste se il periodo richiesto va dal ", formatter(start), " al ", formatter(end), "."];
        case 2:
            return ["Non inviarmi richieste se la lunghezza del soggiorno è ", formatter(`minore di ${minNightsLimit} notti.`)];
        case 3:
            return ["Non inviarmi richieste se il numero di persone è ", formatter(`minore di ${minPeopleLimit}.`)];
        case 4:
            return ["Non inviarmi richieste se il numero di persone è ", formatter(`maggiore di ${maxPeopleLimit}.`)];
        case 5:
            return maxPetsLimit === 0
                ? ["Non inviarmi richieste se l'ospite ha animali domestici."]
                : ["Non inviarmi richieste se il numero di animali domestici è ", formatter(`maggiore di ${maxPetsLimit}.`)];
        case 10:
            return ["Non inviarmi richieste se il numero di posti letto liberi non è sufficiente per il numero di persone per il periodo richiesto."];
        case 52:
            return ["Non inviarmi richieste se la lunghezza del soggiorno è ", formatter(`minore di ${minNightsLimit} notti`), " e se il periodo richiesto va dal ", formatter(start), " al ", formatter(end), "."];
        case 53:
            return ["Non inviarmi richieste se il numero di persone è ", formatter(`minore di ${minPeopleLimit}`), " e se il periodo richiesto va dal ", formatter(start), " al ", formatter(end), "."];
        case 54:
            return ["Non inviarmi richieste se il numero di persone è ", formatter(`maggiore di ${maxPeopleLimit}`), " e se il periodo richiesto va dal ", formatter(start), " al ", formatter(end), "."];
        case 55:
            return ["Non inviarmi richieste se il numero di animali è ", formatter(`maggiore di ${maxPetsLimit}`), " e se il periodo richiesto va dal ", formatter(start), " al ", formatter(end), "."];
        default:
            console.error(`No meaning for the rule with type: ${ruleType}`, JSON.stringify(rule));
            return [`No meaning for the rule with type: ${ruleType}`];
    }
};

const getTemporalRuleDescription = (rule) => {
    const {ruleType, minNightsLimit, minPeopleLimit, maxPeopleLimit, maxPetsLimit} = rule;

    switch (ruleType) {
        case 2:
            return null;
        case 52:
            return `Minimo ${minNightsLimit} notti`;
        case 53:
            return `Minimo ${minPeopleLimit} persone`;
        case 54:
            return `Massimo ${maxPeopleLimit} persone`;
        case 55:
            return `Massimo ${maxPetsLimit} animali domestici`;
        default:
            return null;
    }
};

const validateRule = (rule) => {
    const {ruleType, startDay, endDay, minNightsLimit, minPeopleLimit, maxPeopleLimit, maxPetsLimit} = rule;

    const startDate = startDay ? DateParser.safeParseISO(startDay) : null;
    const endDate = endDay ? DateParser.safeParseISO(endDay) : null;

    const nights = differenceInCalendarDays(endDate, startDate);

    let periodErrors = [];
    let valueErrors = [];
    switch (ruleType) {
        case 1:
            periodErrors = periodValidator(startDate, endDate);
            break;
        case 2:
            valueErrors = numberValidator(2, 60)(minNightsLimit);
            break;
        case 3:
            valueErrors = numberValidator(1, 20)(minPeopleLimit);
            break;
        case 4:
            valueErrors = numberValidator(1, 20)(maxPeopleLimit);
            break;
        case 5:
            valueErrors = numberValidator(0, 3)(maxPetsLimit);
            break;
        case 10:
            break;
        case 52:
            valueErrors = numberValidator(2, nights)(minNightsLimit);
            periodErrors = periodValidator(startDate, endDate);
            break;
        case 53:
            valueErrors = numberValidator(1, 20)(minPeopleLimit);
            periodErrors = periodValidator(startDate, endDate);
            break;
        case 54:
            valueErrors = numberValidator(1, 20)(maxPeopleLimit);
            periodErrors = periodValidator(startDate, endDate);
            break;
        case 55:
            valueErrors = numberValidator(0, 3)(maxPetsLimit);
            periodErrors = periodValidator(startDate, endDate);
            break;
        default:
            console.error(`Impossible to valide the rule with type ${ruleType}`, JSON.stringify(rule));
    }

    return {
        periodErrors,
        valueErrors
    }
};

const periodValidator = (start, end) => {
    let errors = [];

    if (!isValid(start)) {
        errors.push("Data inizio non valida");
        return errors;
    }

    if (!isValid(end)) {
        errors.push("Data fine non valida");
        return errors;
    }

    if (!isBefore(start, end)) {
        errors.push("La data di inizio deve essere antecedente alla data di fine")
    }

    return errors;
};

const numberValidator = (min, max) => (value) => {
    let errors = [];

    if (!Number.isInteger(value)) {
        errors.push(`Inserire un numero`);
        return errors
    }

    if (value < min) {
        errors.push(`Deve essere maggiore o uguale a ${min}`)
    }

    if (value > max) {
        errors.push(`Deve essere minore o uguale a ${max}`)
    }

    return errors
};

export {
    FILTER_KEYS,
    filterAttributes,
    selectMinNightsRule,
    selectMinPeopleRule,
    selectMaxPeopleRule,
    selectMaxPetsRule,
    selectBookingRule,
    getInitialRule,
    getRulePeriod,
    getRuleMeaning,
    getTemporalRuleDescription,
    validateRule
}